.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  font-size: var(--text-caption);
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  transition:
    background var(--motion-instant),
    border-color var(--motion-instant),
    color var(--motion-instant);
}

.sm {
  height: 28px;
  padding: 0 10px;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
}

.lg {
  height: 44px;
  padding: 0 20px;
  font-size: var(--text-callout);
}

.primary {
  background: var(--c-brand);
  color: var(--c-on-brand);
}

.primary:hover:not(:disabled) {
  background: var(--c-brand-vivid);
  color: var(--c-on-brand);
}

.secondary {
  background: transparent;
  border-color: var(--c-border);
  color: var(--c-text-primary);
}

.secondary:hover:not(:disabled) {
  background: var(--c-overlay);
  border-color: var(--c-border-strong);
  color: var(--c-text-primary);
}

.ghost {
  background: transparent;
  color: var(--c-text-secondary);
}

.ghost:hover:not(:disabled) {
  background: var(--c-overlay);
  color: var(--c-text-primary);
}

.danger {
  background: transparent;
  border-color: color-mix(in srgb, var(--c-negative) 40%, transparent);
  color: var(--c-negative);
}

.danger:hover:not(:disabled) {
  background: var(--c-negative-subtle);
  color: var(--c-negative);
}

.link {
  height: auto;
  padding: 0;
  background: none;
  color: var(--c-brand-muted);
}

.link:hover:not(:disabled) {
  color: var(--c-brand-light);
}

.button:disabled,
.button[aria-disabled='true'] {
  opacity: 0.5;
}

.block {
  width: 100%;
}

.spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* `IconButton`. Every square icon control in the app is one — menu triggers, modal and toast
   closes, row removes, chart hide/show toggles, the Today view switch — so a class missing here
   leaves all of them as the browser's default grey button. */
.iconButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  background: transparent;
  color: var(--c-text-secondary);
}

.iconButton:hover:not(:disabled) {
  background: var(--c-overlay);
  color: var(--c-text-primary);
}

.iconButton.outlined {
  border-color: var(--c-border);
}

.iconButton.sm {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: var(--radius-sm);
}

.iconButton.active {
  background: var(--c-brand-subtle);
  color: var(--c-brand-muted);
}

/* The AI glow (`aiWorking`): while the assistant writes, the landing headline's four colours run
   round the button's border as a turning gradient line, with a faint shadow of the same colours
   outside it. Neither touches the face or the label: the line is masked to the 1px border, and an
   outer box-shadow is never painted under the button. The phone's `AiGlow` is the same effect. The
   colours are `Palette.ai*` via tokens.css and are used for nothing else, which keeps this apart
   from the blue "live" glow. */
@property --ai-angle {
  syntax: '<angle>';
  inherits: true;
  initial-value: 0deg;
}

.aiWorking {
  --ai-r: 4px;
  --ai-blur: 10px;
  --ai-mix: 40%;
  position: relative;
  cursor: progress;
  animation: ai-turn var(--motion-ai-glow) linear infinite;
  box-shadow:
    calc(cos(var(--ai-angle)) * var(--ai-r)) calc(sin(var(--ai-angle)) * var(--ai-r)) var(--ai-blur)
      color-mix(in srgb, var(--c-ai-sky) var(--ai-mix), transparent),
    calc(cos(var(--ai-angle) + 90deg) * var(--ai-r)) calc(sin(var(--ai-angle) + 90deg) * var(--ai-r))
      var(--ai-blur) color-mix(in srgb, var(--c-ai-blue) var(--ai-mix), transparent),
    calc(cos(var(--ai-angle) + 180deg) * var(--ai-r)) calc(sin(var(--ai-angle) + 180deg) * var(--ai-r))
      var(--ai-blur) color-mix(in srgb, var(--c-ai-violet) var(--ai-mix), transparent),
    calc(cos(var(--ai-angle) + 270deg) * var(--ai-r)) calc(sin(var(--ai-angle) + 270deg) * var(--ai-r))
      var(--ai-blur) color-mix(in srgb, var(--c-ai-teal) var(--ai-mix), transparent);
}

/* The line: a conic gradient on the same turning angle, masked down to the border. */
.aiWorking::after {
  content: '';
  position: absolute;
  inset: -1px;
  padding: 1.5px;
  border-radius: inherit;
  background: conic-gradient(
    from var(--ai-angle),
    var(--c-ai-sky),
    var(--c-ai-blue),
    var(--c-ai-violet),
    var(--c-ai-teal),
    var(--c-ai-sky)
  );
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

/* Busy, not unavailable: the button keeps its own look rather than the disabled fade. */
.aiWorking:disabled {
  opacity: 1;
}

[data-theme='light'] .aiWorking {
  --ai-mix: 30%;
}

/* The global reduced-motion rule shortens animations rather than stopping them, which on a looping
   angle would flicker; the line and glow simply hold still instead. */
@media (prefers-reduced-motion: reduce) {
  .aiWorking,
  .aiWorking::after {
    animation: none;
  }
}

@keyframes ai-turn {
  to {
    --ai-angle: 360deg;
  }
}
